import React, { useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useTypedSelector } from 'store/index';
import { Account, fetchAccounts } from 'store/user';
import { fetchAppInfo } from 'store/application';
import { post } from 'store/request';
import { Loading, message } from '@tencent/spaui';
import Table, { renderEllipsis, TableProps } from 'components/table';
import { report } from 'store/report';
import Bottom from 'components/auth/bottom';
import Tooltip from 'components/tooltip';

const licenses = [
  {
    url: 'https://connect.qq.com/devuser.html?agreement#/',
    track: 'link-qq-license',
    label: 'QQ登录服务协议',
  },
  {
    url: 'https://developers.e.qq.com/developer_platform_advertiser_protocol',
    track: 'link-developer-license',
    label: '腾讯广告开发者平台广告主服务协议',
  },
];

const singleHeaders: TableProps['headers'] = [
  {
    label: '账户ID',
    name: 'account_id',
  },
  {
    label: '账户名称',
    name: 'nick_name',
  },
];

const multipleHeaders: TableProps['headers'] = [
  {
    label: '身份',
    name: 'role_name',
    width: 146,
    filterable: true,
    filterSource: [
      { label: '代理商', value: '代理商' },
      { label: '直客', value: '直客' },
    ],
  },
  {
    label: '服务商ID',
    name: 'main_account_id',
    width: 146,
  },
  {
    label: '直客/子客ID',
    name: 'account_id',
    width: 146,
  },
  {
    label: '账户名称',
    name: 'nick_name',
    width: 216,
    render: renderEllipsis(200),
  },
];

const sameEnterpriseHeaders: TableProps['headers'] = [
  {
    label: '主体',
    name: 'enterprise_name',
    width: 168,
    autoRowSpan: true,
    tooltip: '该应用仅支持同一主体下的账户批量授权',
    render: renderEllipsis(142),
  },
  {
    label: '身份',
    name: 'role_name',
    width: 80,
    filterable: true,
    filterSource: [
      { label: '代理商', value: '代理商' },
      { label: '直客', value: '直客' },
    ],
  },
  {
    label: '服务商ID',
    name: 'main_account_id',
    width: 120,
  },
  {
    label: '直客/子客ID',
    name: 'account_id',
    width: 120,
  },
  {
    label: '账户名称',
    name: 'nick_name',
    width: 168,
    render: renderEllipsis(142),
  },
];

const selectedHeaders = [
  {
    label: '服务商ID',
    name: 'main_account_id',
    width: 82,
  },
  {
    label: '直客/子客ID',
    name: 'account_id',
    width: 94,
  },
  {
    label: '账户名称',
    name: 'nick_name',
    width: 108,
  },
  {
    label: '操作',
    name: 'operation',
    width: 52,
  },
];

interface AppAuthProps {
  id: string;
  url?: string;
  newWin?: boolean;
}

const AppAuthDialog: React.FC<AppAuthProps> = ({ id, url, newWin }) => {
  const [showMultiple, setShowMultiple] = useState<boolean>();
  const [selected, setSelected] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>();
  const dispatch = useAppDispatch();
  const {
    user: { accounts, accountsLoading, lastSelected, user },
    application: { list, appInfoMap, appInfoLoading },
  } = useTypedSelector((state) => state);
  const { app_icon, app_name, category, redirect_uri, state, same_enterprise, provider } = list?.[id] ?? {};

  const selectedData = useMemo(() => {
    return selected.map((id) => accounts.find(({ account_id }) => account_id === id)).filter((item) => !!item);
  }, [accounts, selected]);

  useEffect(() => {
    dispatch(fetchAccounts(id));
    if (!appInfoMap?.[id]) {
      dispatch(fetchAppInfo(id));
    }
  }, []);

  useEffect(() => {
    setSelected(lastSelected.filter((id) => accounts.find(({ account_id }) => account_id === id)));
  }, [accounts, lastSelected]);

  const canOptional = ({ enterprise_name }: Account) => {
    if (!same_enterprise || !selected.length) {
      return true;
    }

    return enterprise_name === selectedData[0].enterprise_name;
  };

  const handleSelected = (value: string[]) => {
    if (!same_enterprise) {
      setSelected(value);
      return;
    }

    const ret: string[] = [];
    let enterprise: string = null;
    for (const id of value) {
      const { enterprise_name } = accounts.find(({ account_id }) => account_id === id);
      if (enterprise === null || enterprise === enterprise_name) {
        enterprise = enterprise_name;
        ret.push(id);
      } else {
        break;
      }
    }
    setSelected(ret);
  };

  const handleConfirm = () => {
    if (loading) {
      return;
    }

    const idList = showMultiple ? selected : accounts.map(({ account_id }) => account_id);
    const list = idList?.map((item) => {
      const find = accounts.find(({ account_id }) => account_id === item);
      return {
        account_id: find?.account_id,
        account_type: find?.account_type,
      };
    });

    if (list.length > 100) {
      message.error('最多只能选择100个！');
      return;
    }
    if (!list.length) {
      message.error('请选择账户');
      return;
    }

    setLoading(true);
    const params = {
      auth_account_type: user.login_type === 1 ? 'ACCOUNT_TYPE_QQ' : 'ACCOUNT_TYPE_WECHAT',
      client_id: id,
      redirect_uri: url || redirect_uri,
      use_authorization_code_list: 0,
      scope: appInfoMap[id]?.map(({ scope }) => scope)?.join(),
      account_list: list,
      link_from: 'isv',
      state: newWin ? state : new URLSearchParams(location.search).get('state') || state,
    };
    const win = newWin ? window.open() : window;
    win.opener = null;
    post(`/developer_service${category === 2 ? '/rta' : ''}/authorize_batch`, params)
      .then((res) => {
        report({ event: 'request-app-auth', client_id: id, url: res.redirect_uri, payload: JSON.stringify(params) });
        win.location.href = res.redirect_uri;
      })
      .catch((err) => {
        newWin && win.close();
        message.error(err);
      })
      .finally(() => newWin && setLoading(false));
  };

  const bottom = (
    <Bottom
      id="app-auth"
      clientId={id}
      licenses={licenses}
      loading={loading}
      btnLabel="同意授权"
      onConfirm={handleConfirm}
    />
  );

  const renderSelectedCell = (colName: string, account: Account) => {
    if (colName === 'operation') {
      return (
        <span
          role="button"
          className="cancel"
          onClick={() => setSelected(selected.filter((item) => item !== account.account_id))}
        >
          取消
        </span>
      );
    }
    if (colName === 'nick_name') {
      return renderEllipsis(92)('data', '', account.nick_name);
    }
    return (account as any)[colName];
  };

  if (showMultiple) {
    return (
      <>
        <div className="accounts-container">
          <Table
            selectableCol="account_id"
            bordered={!!same_enterprise}
            striped={!same_enterprise}
            headers={same_enterprise ? sameEnterpriseHeaders : multipleHeaders}
            topContent={
              <Tooltip
                popover={
                  <>
                    仅所属服务商或直客已开通的账户ID/子客ID可供选择，您可前往
                    <a href="/application-list" style={{ margin: '0 4px' }}>
                      管理中心-开通详情
                    </a>
                    查看所有账户ID的开通情况
                  </>
                }
              >
                <div className="text-bold">
                  选择授权的账户
                  <span className="sub">(最多只能选择 100 个)</span>
                </div>
              </Tooltip>
            }
            canOptional={canOptional}
            searchableCols={['main_account_id', 'account_id', 'nick_name']}
            data={accounts}
            selected={selected}
            onSelected={handleSelected}
          />

          <div className="split" />

          <div className="selected">
            <div className="text-bold">
              已选择 {selected.length} 个账户
              <span className="cancel" role="button" onClick={() => setSelected([])}>
                全部取消
              </span>
            </div>
            <div className="table">
              <div className="thead">
                <div className="tr">
                  {selectedHeaders.map(({ label, name, width }) => (
                    <div key={name} className="th" style={{ width }}>
                      {label}
                    </div>
                  ))}
                  <div className="gutter" />
                </div>
              </div>
              <div className="tbody">
                {selectedData.map((item) => (
                  <div key={item.account_id} className="tr">
                    {selectedHeaders.map(({ name, width }) => (
                      <div key={name} className="td" style={{ width }}>
                        {renderSelectedCell(name, item)}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        {bottom}
      </>
    );
  }

  return (
    <>
      <div className="app">
        <img src={app_icon} alt="" />
        <div className="text-bold">{app_name}</div>
      </div>
      <div className="notice">该应用由{provider}提供，将允许应用进行以下操作：</div>

      {appInfoLoading && <Loading />}

      {appInfoMap?.[id]?.map(({ name, desc }) => (
        <React.Fragment key={name}>
          <div className="text-bold">{name}</div>
          <div className="text-desc">{desc}</div>
        </React.Fragment>
      ))}

      {accountsLoading ? (
        <div className="stick-bottom">
          <Loading />
        </div>
      ) : (
        <>
          {accounts?.length === 1 && (
            <>
              <hr />
              <div className="notice">授权账户</div>
              <Table headers={singleHeaders} data={accounts} bottomSpaceElement={<></>} />
              {bottom}
            </>
          )}

          {accounts?.length > 1 && (
            <div className="stick-bottom">
              <span role="button" className="rect-button" onClick={() => setShowMultiple(true)}>
                选择授权账户
              </span>
            </div>
          )}
        </>
      )}
    </>
  );
};

export default AppAuthDialog;
